const API_BASE_URL =
  import.meta.env.VITE_AUTH_API_URL || 'http://localhost:3005/api/v1'

export interface User {
  id: string
  email: string
  firstName: string
  lastName: string
  roleId: number
  roleName: string
  organizationId: string
  organizationName: string
  statusId: number
  statusName: string
  failedLoginAttempts?: number
  lockedUntil?: string | null
  lastLoginAt?: string
  passwordChangedAt?: string
  twoFactorEnabled?: boolean
  profileData?: Record<string, any>
  preferences?: Record<string, any>
  createdAt: string
  updatedAt: string
}

export interface UserListResponse {
  success: boolean
  data: {
    users: User[]
    pagination: {
      page: number
      limit: number
      total: number
    }
  }
}

export interface UserDetailsResponse {
  success: boolean
  data: User
}

export interface InviteUserRequest {
  email: string
  roleId: number
  organizationId: string
  invitationMessage?: string
}

export interface InviteUserResponse {
  success: boolean
  data?: {
    invitation: {
      id: string
      email: string
      organizationId: string
      status: string
      expiresAt: string
    }
    message: string
  }
  error?: {
    code: string
    message: string
  }
}

export interface UpdateUserRequest {
  firstName?: string
  lastName?: string
  roleId?: number
  statusId?: number
  profileData?: Record<string, any>
  preferences?: Record<string, any>
}

export interface ChangePasswordRequest {
  currentPassword?: string
  newPassword: string
  userId?: string
}

export interface ApiResponse {
  success: boolean
  message?: string
  error?: {
    code: string
    message: string
    field?: string
  }
}

export interface UserListParams {
  page?: number
  limit?: number
  search?: string
  organizationId?: string
  roleId?: number
  statusId?: number
  excludeSupport?: boolean
}

class UserService {
  async getUsers(params: UserListParams = {}): Promise<UserListResponse> {
    const searchParams = new URLSearchParams()

    if (params.page) searchParams.append('page', params.page.toString())
    if (params.limit) searchParams.append('limit', params.limit.toString())
    if (params.search) searchParams.append('search', params.search)
    if (params.organizationId)
      searchParams.append('organizationId', params.organizationId)
    if (params.roleId) searchParams.append('roleId', params.roleId.toString())
    if (params.statusId)
      searchParams.append('statusId', params.statusId.toString())
    if (params.excludeSupport)
      searchParams.append('excludeSupport', 'true')

    const queryString = searchParams.toString()
    const url = `${API_BASE_URL}/users${queryString ? `?${queryString}` : ''}`

    const response = await fetch(url, {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json'
      }
    })

    return await response.json()
  }

  async getUser(id: string): Promise<UserDetailsResponse> {
    const response = await fetch(`${API_BASE_URL}/users/${id}`, {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json'
      }
    })

    return await response.json()
  }

  async inviteUser(data: InviteUserRequest): Promise<InviteUserResponse> {
    const response = await fetch(`${API_BASE_URL}/users/invite`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    })

    return await response.json()
  }

  async updateUser(id: string, data: UpdateUserRequest): Promise<ApiResponse> {
    const response = await fetch(`${API_BASE_URL}/users/${id}`, {
      method: 'PUT',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    })

    return await response.json()
  }

  async deleteUser(id: string): Promise<ApiResponse> {
    const response = await fetch(`${API_BASE_URL}/users/${id}`, {
      method: 'DELETE',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json'
      }
    })

    return await response.json()
  }

  async resetUserPassword(id: string): Promise<ApiResponse> {
    const response = await fetch(`${API_BASE_URL}/users/${id}/reset-password`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json'
      }
    })

    return await response.json()
  }

  async changePassword(data: ChangePasswordRequest): Promise<ApiResponse> {
    const response = await fetch(`${API_BASE_URL}/users/change-password`, {
      method: 'PUT',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    })

    return await response.json()
  }

  async sendVerificationEmail(id: string): Promise<ApiResponse> {
    const response = await fetch(
      `${API_BASE_URL}/users/${id}/send-verification`,
      {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      }
    )

    return await response.json()
  }

  async disableUser(organizationId: string, userId: string): Promise<ApiResponse> {
    const response = await fetch(
      `${API_BASE_URL}/organizations/${organizationId}/users/${userId}/status`,
      {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          status: 'inactive',
          reason: 'Disabled by administrator'
        })
      }
    )

    return await response.json()
  }

  async enableUser(organizationId: string, userId: string): Promise<ApiResponse> {
    const response = await fetch(
      `${API_BASE_URL}/organizations/${organizationId}/users/${userId}/status`,
      {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          status: 'active',
          reason: 'Enabled by administrator'
        })
      }
    )

    return await response.json()
  }
}

export const userService = new UserService()
